import { Helper } from "../core/util/helper";
import { HTTPService, JsonResultClass } from "../core/util/httpManager";
import { Grid } from "../core/components/grid";
import { Dialog, DialogProps } from "../core/components/dialog";
import { Config } from "../core/config/config";
import { ComponentHelper } from "../core/util/componentHelper";
import { TextBox } from "../core/components/textBox";
import { IsSearchField } from "../core/components/isSearchField";
import { NotificationHelper } from "../core/util/notificationHelper";
import { DialogHelper } from "../core/util/dialogHelper";
import { IconsComboBox } from "../core/components/iconComboBox";
import { GridCustomToolBarButton } from "../core/components/toolBar";
import React from "react";
import ReactDOM from "react-dom";
interface DashboardState {
    selected: string;
}

interface DashboardProps {
    entity: {
        DashboardStatistics: DashboardStatistic[];
    };
    onChange: (field, value) => void;
}

export default class Dashboard extends React.Component<DashboardProps, DashboardState>{
    private gridOptions: kendo.ui.GridOptions;
    private gridId = kendo.guid();
    private usedStatistics: string[] = [];
    private statistics: Statistic[] = [];

    private ADD = "add";
    private REMOVE = "remove";
    private OPEN_DIALOG = "open_dialog";
    private UP = "up";
    private DOWN = "down";

    constructor(props) {
        super(props)
        this.state = {
            selected: null
        }
        this.init();
    }

    private init = async () => {
        const [statistics, usedStatistics] = await Promise.all([
            this.getStatistics(),
            this.getUsedStatistics()
        ]);
        this.usedStatistics = usedStatistics;
        this.statistics = statistics;
        this.initGridOptions();
        this.setState({})
    }

    private getStatistics = async () => {
        const result = await HTTPService.get<any[]>('Statistic/api/StatisticUi/Dummy/GetAllStatisticsUngrouped');
        return result.map((st) => {
            return {
                id: st.Id,
                name: st.Name,
                translationKey: (st.UntranslatedName as string).replace("{{{", "").replace("}}}", "")
            };
        });
    }

    private getGridData = () => {
        return this.props.entity.DashboardStatistics;
    }

    private getSelected = () => {
        const data = this.getGridData();
        return data.find(ds => ds.Id == this.state.selected)
    }

    private update = (data: DashboardStatistic[]) => {
        this.props.onChange({ model: "entity.DashboardStatistics" }, data)
    }

    private getUsedStatistics = async () => {
        const result = await HTTPService.get<JsonResultClass<string[]>>("Dashboard/GetUsedStatistics");
        if (result.Code == 0) {
            const data = result.Data;
            return data;
        }
        return [];
    }

    private initGridOptions = () => {
        this.gridOptions = {
            sortable: false,
            pageable: false,
            groupable: false,
            filterable: false,
            selectable: true,
            resizable: true,
            scrollable: true,
            editable: false,
            columns: [
                {
                    field: "StatisticId",
                    title: "StatisticId",
                    hidden: true
                },
                {
                    field: "Name",
                    title: Helper.getTranslation("WebSMS.UserSettings.Dashboard.Statistic"),
                    template: (dataItem: DashboardStatistic) => {
                        const item = this.statistics.find(st => st.id == dataItem.StatisticId)
                        return `<span>${item ? item.name : dataItem.StatisticId}</span>`;
                    }
                },
                {
                    field: "LabelTranslationKey",
                    title: Helper.getTranslation("WebSMS.UserSettings.Dashboard.TabName"),
                    template: (dataItem: DashboardStatistic) => {
                        return `<span>${(Helper.getTranslation(dataItem.TabName) ?? dataItem.TabName)}</span>`;
                    }
                },
                {
                    field: "Icon",
                    title: Helper.getTranslation("WebSMS.ResourcePlanningScheduler.Icon.Icon"),
                    width: 80,
                    template: (dataItem: DashboardStatistic) => {
                        return `<div style='display: flex; justify-content: center; align-items: center;'><i class="fa-light fa-${dataItem.Icon} fa-2x"></i></div>`;
                    }
                }
            ]
        };
    }

    private renderGrid = () => {
        if (!this.gridOptions)
            return null;
        const data = this.getGridData();
        const selected = this.getSelected();
        const buttons: Array<GridCustomToolBarButton> = [
            {
                iconClass: "fa-solid fa-plus",
                autoEnabledOnRecordSelected: false,
                caption: Helper.getTranslation("WebSMS.UserSettings.Dashboard.New"),
                enabled: true,
                id: this.ADD
            },
            {
                iconClass: "fa-solid fa-pen",
                autoEnabledOnRecordSelected: false,
                caption: Helper.getTranslation("Standard.Editieren"),
                enabled: Helper.hasValue(selected),
                id: this.OPEN_DIALOG
            },
            {
                iconClass: "fa-solid fa-xmark",
                autoEnabledOnRecordSelected: false,
                caption: Helper.getTranslation("WebSMS.UserSettings.Dashboard.RemoveStatistic"),
                enabled: Helper.hasValue(selected),
                id: this.REMOVE
            },
            {
                iconClass: "fa-solid fa-up",
                autoEnabledOnRecordSelected: false,
                caption: Helper.getTranslation("Standard.Up"),
                enabled: Helper.hasValue(selected) && selected.Pos > 0,
                id: this.UP
            },
            {
                iconClass: "fa-solid fa-down",
                autoEnabledOnRecordSelected: false,
                caption: Helper.getTranslation("Standard.Down"),
                enabled: Helper.hasValue(selected) && selected.Pos < (data.length - 1),
                id: this.DOWN
            }
        ];


        return (
            <div className="flex-container" id={this.gridId}>
                <Grid
                    data={data}
                    options={this.gridOptions}
                    id="dashboard_global"
                    customButtons={buttons}
                    onClickCustomButton={this.onButtonClick}
                    onSelectionChanged={this.onSelectionChanged}
                    selectFilter={Helper.hasValue(selected) ? this.statistics.find(st => st.id == selected.StatisticId)?.name : null}
                    throttleActive={false}
                />
            </div>
        );
    }

    private onSelectionChanged = (dataItem: DashboardStatistic) => {
        if (Helper.hasValue(dataItem))
            this.setState({ selected: dataItem.Id })
    }

    private onButtonClick = async (id: string) => {
        const selected = this.getSelected();
        switch (id) {
            case this.ADD:
                await this.add();
                break;
            case this.OPEN_DIALOG:
                await this.edit(selected);
                break;
            case this.REMOVE:
                await this.remove(selected);
                break;
            case this.UP:
                this.swap(selected.Pos, selected.Pos - 1);
                break;
            case this.DOWN:
                this.swap(selected.Pos, selected.Pos + 1);
                break;
        }
    }

    private add = async () => {
        const statistic = await open(null, Helper.getTranslation("WebSMS.UserSettings.Dashboard.New"));
        if (!statistic) return;
        const data = this.getGridData();
        const pos = data.length == 0 ? 0 : Math.max(...data.map(st => st.Pos)) + 1;
        statistic.Pos = pos;
        data.push(statistic);
        this.update(data);
    }

    private edit = async (dataItem: DashboardStatistic) => {
        const statistic = await open(dataItem, this.statistics.find(st => st.id == dataItem.StatisticId).name);
        if (!statistic) return;
        const data = this.getGridData();
        const index = data.findIndex(s => s.Id == statistic.Id);
        data[index] = statistic;
        this.update(data);
    }

    private remove = async (dataItem: DashboardStatistic) => {
        const msg = Helper.hasValue(this.usedStatistics.find(id => dataItem.Id == id)) ? Helper.getTranslation("WebSMS.UserSettings.Dashboard.IsUsed") : Helper.getTranslation("WebSMS.UserSettings.Dashboard.Remove");
        const isOk = await DialogHelper.confirm(msg);
        if (!isOk) return;
        let data = this.getGridData();
        data = data.filter(item => item.Id != dataItem.Id);
        for (let i = 0; i < data.length; i++)
            data[i].Pos = i;
        this.update(data);
    }

    private swap = (posA: number, posB: number) => {
        const data = this.getGridData();

        const indexA = data.findIndex(ds => ds.Pos == posA);
        const indexB = data.findIndex(ds => ds.Pos == posB);

        const itemA = data[indexA];
        const itemB = data[indexB];

        itemA.Pos = posB;
        itemB.Pos = posA;

        data[indexA] = itemB;
        data[indexB] = itemA;

        this.update(data);
    }

    render() {
        return this.renderGrid();
    }
}

interface Statistic {
    id: string;
    name: string;
    translationKey: string;
}

const open = (dashboardStatistic: DashboardStatistic = null, title: string = null) => {
    return new Promise<DashboardStatistic>((resolve) => {

        const targetElement = $("<div>");
        $("body").append(targetElement);
        const root = ReactDOM["createRoot"](targetElement[0]);

        const removeDialog = () => {
            root.unmount(targetElement[0]);
            targetElement[0].remove();
        }

        const onOk = (dashboardStatistic: DashboardStatistic) => {
            removeDialog();
            resolve(dashboardStatistic)
        }

        const onCancel = () => {
            removeDialog();
            resolve(null)
        }
        const isNew = !Helper.hasValue(dashboardStatistic);
        if (isNew) {
            dashboardStatistic = {
                Icon: "chart-column",
                Id: kendo.guid(),
                TabName: null,
                Permission: 2,
                Pos: -1,
                StatisticId: null
            }
        }


        root.render(<DashboardDialog onOk={onOk} dashboardStatistic={dashboardStatistic} dialogTitle={title ?? ""} onCancel={onCancel} isNew={isNew} />);
    });
}



interface DashboardDialogProps {
    onOk: (statistic: DashboardStatistic) => void;
    onCancel: () => void;
    dashboardStatistic: DashboardStatistic;
    dialogTitle: string;
    isNew: boolean;
}

interface DashboardDialogState extends DashboardStatistic {
    statistic?: any;
    //Wenn der Name händisch gesetzt worden ist, soll er nicht durch die Statistiksuche übersteuert werden
    nameWasSet: boolean;
}

class DashboardDialog extends React.Component<DashboardDialogProps, DashboardDialogState>{
    private factory = ComponentHelper.createFactory();
    private dialogProps: DialogProps = {
        title: "",
        open: true,
        actions: [
            {
                label: Config.translations.cancel,
                action: this.props.onCancel
            },
            {
                label: Config.translations.ok,
                action: () => {
                    if (this.props.isNew && Helper.isNullOrEmpty(this.state.StatisticId)) {
                        NotificationHelper.showWarningMessage(Helper.getTranslation("WebSMS.UserSettings.Dashboard.SelectStatistic"));
                        return false;
                    }

                    if (Helper.isNullOrEmpty(this.state.Icon)) {
                        NotificationHelper.showWarningMessage(Helper.getTranslation("WebSMS.UserSettings.Dashboard.MissingIcon"));
                        return false;
                    }
                    if (Helper.isNullOrEmpty(this.state.TabName)) {
                        NotificationHelper.showWarningMessage(Helper.getTranslation("WebSMS.UserSettings.Dashboard.MissingName"));
                        return false;
                    }
                    const result: DashboardStatistic = {
                        Icon: this.state.Icon,
                        Id: this.state.Id,
                        TabName: this.state.TabName,
                        Permission: this.state.Permission,
                        Pos: this.state.Pos,
                        StatisticId: this.state.StatisticId
                    };
                    this.props.onOk(result)
                },
                primary: true
            }
        ],
        afterClose: () => {
        },
        width: 750
    };

    constructor(props: DashboardDialogProps) {
        super(props)
        this.state = {
            Icon: props.dashboardStatistic.Icon,
            TabName: props.dashboardStatistic.TabName,
            Id: props.dashboardStatistic.Id,
            Permission: props.dashboardStatistic.Permission,
            Pos: props.dashboardStatistic.Pos,
            StatisticId: props.dashboardStatistic.StatisticId,
            statistic: null,
            nameWasSet: false
        };

    }

    private renderIconTextBox = () => {
        const row = this.props.isNew ? 3 : 2;
        const gridRow = `${row}/${row + 1}`

        return (
            <>
                <div style={{ display: "flex", alignItems: "center", gridColumn: "1/2", gridRow: gridRow }}>
                    {Helper.getTranslation("WebSMS.ResourcePlanningScheduler.Icon.Icon")}
                </div>
                <div style={{ display: "flex", alignItems: "center", gridColumn: "2/3", gridRow: gridRow }}>
                    <IconsComboBox value={this.state.Icon} onChange={(value) => { this.setState({ Icon: value }) }} />
                </div>
                <div style={{ display: "flex", alignItems: "center", gridColumn: "3/4", gridRow: gridRow }}>
                    <i className={`fa-light fa-${this.state.Icon} fa-2x`} style={{ marginLeft: "10px" }} ></i>
                </div>
            </>
        );
    }

    private renderTabNameTextBox = () => {
        const row = this.props.isNew ? 2 : 1;
        const gridRow = `${row}/${row + 1}`
        const props = this.factory.getDefaultTextBoxProps();
        props.value = this.state.TabName;
        props.field.triggerOnChange = true;
        props.onChange = (_, value: string) => {
            this.setState({ TabName: value, nameWasSet: true })
        }

        return (
            <>
                <div style={{ display: "flex", alignItems: "center", gridColumn: "1/2", gridRow: gridRow }}>
                    {Helper.getTranslation("WebSMS.UserSettings.Dashboard.TabName") + " (" + Helper.getTranslation("WebSMS.UserSettings.Dashboard.TranslationKey") + ")"}
                </div>
                <div style={{ display: "flex", alignItems: "center", gridColumn: "2/3", gridRow: gridRow }}>
                    <TextBox {...props} />
                </div>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gridColumn: "3/4", gridRow: gridRow, marginLeft: "10px" }} title={Helper.getTranslation(this.state.TabName)}>
                    <label style={{ flex: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} >{Helper.getTranslation(this.state.TabName)}</label>
                </div>
            </>
        );
    }

    private renderAutoComplete = () => {
        if (!this.props.isNew)
            return null;

        const props = this.factory.getDefaultSearchFieldProps();
        props.value = Helper.hasValue(this.state.statistic) ? this.state.statistic.Name : "";
        props.field.autocompleteEnabled = true;
        props.field.searchButtonEnabled = false;
        props.field.listWidth = 500;
        props.field.autocompleteTemplate = "<table cellspacing='0' style='table-layout:fixed; width:500px'><colgroup><col style='width: 500px' /></colgroup><tr><td style='vertical-align:top'>${data.Name}</td></tr></table>";
        props.field.autocompleteUrl = "Dashboard/FindStatistic";
        props.field.autocompleteDataTextField = "Name";
        props.context = {
            contextType: "dashboard"
        };
        props.onChange = (_, __, dataItem) => {
            if (Helper.hasValue(dataItem))
                this.setState({ statistic: dataItem, StatisticId: dataItem.Id, TabName: this.state.nameWasSet ? this.state.TabName : dataItem.TranslationKey })
            else
                this.setState({ statistic: null, StatisticId: null, TabName: null })
        }


        return (
            <>
                <div style={{ display: "flex", alignItems: "center", gridColumn: "1/2", gridRow: "1/2" }}>
                    {Helper.getTranslation("WebSMS.UserSettings.Dashboard.Statistic")}
                </div>
                <div style={{ display: "flex", alignItems: "center", gridColumn: "2/3", gridRow: "1/2" }}>
                    <IsSearchField {...props} />
                </div>
            </>
        );

    }

    render() {
        return (
            <>
                <Dialog {...this.dialogProps} title={this.props.dialogTitle} >
                    <div style={{ display: "grid", gridTemplateRows: `repeat(${this.props.isNew ? 3 : 2}, 50px)`, gridTemplateColumns: "2fr 3fr 2fr" }} >
                        {this.renderAutoComplete()}
                        {this.renderTabNameTextBox()}
                        {this.renderIconTextBox()}
                    </div>
                </Dialog >
            </>
        );
    }
}

export interface DashboardStatistic {
    Icon: string;
    Id: string;
    TabName: string;
    Permission: number;
    Pos: number;
    StatisticId: string;
}